import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../../Context/AuthContext' // Use the AuthContext
import Loader from '../../Common/Loader'
import logo from '../assets/logo.svg'
import './Login.css'

function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const [displayedText, setDisplayedText] = useState('')
  const [showCursor, setShowCursor] = useState(true)
  
  const navigate = useNavigate()
  const { signIn, signInWithOAuth, user, loading, isPasswordRecovery } = useAuth()
  
  // Subtitle text for typing animation
  const subtitleText = "Your One-Stop Business Solution"

  // Typing animation effect
  useEffect(() => {
    let currentIndex = 0
    const typingInterval = setInterval(() => {
      if (currentIndex <= subtitleText.length) {
        setDisplayedText(subtitleText.slice(0, currentIndex))
        currentIndex++
      } else {
        clearInterval(typingInterval)
        // Keep cursor blinking after text is complete
        setShowCursor(true)
      }
    }, 50) // Adjust speed here (lower = faster)

    return () => clearInterval(typingInterval)
  }, [])

  // Redirect logic - only redirect if user is authenticated and NOT in password recovery mode
  useEffect(() => {
    if (!loading && user && !isPasswordRecovery) {
      navigate('/dashboard', { replace: true })
    }
  }, [user, loading, isPasswordRecovery, navigate])

  const handleLogin = async (e) => {
    e.preventDefault()
    
    if (!email || !password) {
      setMessage('Please fill in all fields.')
      setMessageType('error')
      return
    }

    setIsLoading(true)
    setMessage('')
    
    try {
      const { data, error } = await signIn(email, password)
      
      if (error) {
        setMessage(error.message)
        setMessageType('error')
      } else if (data) {
        // Success - context will handle the redirect via useEffect
        setMessage('Login successful!')
        setMessageType('success')
      }
    } catch (err) {
      console.error('Login error:', err)
      setMessage('An error occurred during login')
      setMessageType('error')
    } finally {
      setIsLoading(false)
    }
  }

  const handleGoogleLogin = async () => {
    setIsGoogleLoading(true)
    setMessage('')
    
    try {
      const { error } = await signInWithOAuth('google', `${window.location.origin}/dashboard`)
      
      if (error) {
        setMessage(error.message)
        setMessageType('error')
      }
      // Note: For OAuth, the redirect happens automatically, so we don't handle success here
    } catch (err) {
      console.error('Google login error:', err)
      setMessage('An error occurred during Google login')
      setMessageType('error')
    } finally {
      setIsGoogleLoading(false)
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleLogin(e)
    }
  }

  // Show loading if auth is still initializing
  if (loading) {
    return (
      <div className="Login-container">
        <div className="Login-card">
          <Loader />
        </div>
      </div>
    )
  }

  return (
    <div className="Login-container">
      <div className="Login-wrapper">
        {/* Left Section - Image and Subtitle */}
        <div className="Login-left-section">
          <div className="Login-image-container">
            {/* Replace 'your-logo.png' with your actual logo file path */}
            <img 
              src={logo} 
              alt="Logo" 
              className="Login-image"
            />
          </div>
          
          <div className="Login-subtitle-container">
            <p className="Login-subtitle">
              {displayedText}
              {showCursor && <span className="Login-subtitle-cursor"></span>}
            </p>
          </div>
        </div>

        {/* Right Section - Login Form */}
        <div className="Login-right-section">
          <div className="Login-card">
            {(isLoading || isGoogleLoading) && (
              <div className="Login-loading-overlay">
                <Loader />
              </div>
            )}
            
            
            <form className="Login-form" onSubmit={handleLogin}>
              <input 
                className="Login-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Email Address"
                required
                disabled={isLoading || isGoogleLoading}
              />
              
              <input 
                className="Login-input"
                type="password" 
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Password"
                required
                disabled={isLoading || isGoogleLoading}
              />
              
              <button 
                className="Login-button"
                type="submit"
                disabled={isLoading || isGoogleLoading || !email || !password}
              >
                {isLoading ? <Loader /> : 'Sign In'}
              </button>
            </form>

            <hr className="Login-divider" />
            
            <button 
              className="Login-google-button"
              onClick={handleGoogleLogin}
              disabled={isLoading || isGoogleLoading}
            >
              {isGoogleLoading ? <Loader /> : 'Continue with Google'}
            </button>

            {message && (
              <div className={`Login-message Login-message--${messageType}`}>
                {message}
              </div>
            )}

            <div className="Login-links">
              <p className="Login-link-text">
                Don't have an account? <Link to="/signup" className="Login-link">Create Account</Link>
              </p>
              <p className="Login-link-text">
                Forgot your password? <Link to="/forgot-password" className="Login-link">Reset Password</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Login